import axios from 'axios';

import { appConfigStore } from 'Models/AppConfigStore';
import { FERRY_BOOKING_URL } from 'Constants';
import { serialiseDateTimeNoTz } from 'Util/AttributeUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import { BookingStatusTagOption } from 'Views/Components/_HumanWritten/Bookings/BookingStatusTag';
import { getBookingTypesInApp } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/StaffBookingsFilterStore';
import { RouteEntity } from '../../../Models/Entities';
import { bookingTransactionStatus, bookingType } from '../../../Models/Enums';
import { BookingOrderByOption } from './FetchBookingsUtils';

export interface BookingListSummaryDto {
	id: string;
	humanReadableId: string;
	status: bookingTransactionStatus;
	route: RouteEntity;
	tripDateTime: Date;
	totalCost: number;
	isDeparture: boolean;
	createdDate: Date;
	name: string;
	email: string;
	bookingType: bookingType;
	paidPrice: number;
	eventName: string;
	iconName: string;
	cargoDriverDetailsString: string;
	cargoRegoAndTrailerDetailsString: string;
	passengerDetailsString: string;
	startStopId: string;
	endStopId: string;
	freightDetailsString: string;
}

export interface BookingListExportDto {
	id: string;
	humanReadableId: string;
	legacyId?: string;
	createdDate: Date;
	// Who created the booking — the staff member's name, 'Customer' when the account holder booked it
	// themselves, or 'System'. Staff-only, and only populated when that column is selected.
	createdBy?: string;
	// How many times the booking has been changed since it was made — completed alterations only,
	// less the booking's own first one. 0 on an untouched booking.
	alterationCount?: number;
	status: bookingTransactionStatus;
	// Usually a single status (matching `status`), but a cancelled booking that still owes an
	// invoice obligation (awaiting/invoiced cancellation fee) carries both CANCELLED and
	// AWAITING/INVOICED so the row can show both tags, like the booking detailed view.
	transactionStatuses?: bookingTransactionStatus[];
	// Why the booking was cancelled, for the Cancellation Reason column. Absent on a booking that
	// isn't cancelled, and on every booking when that column is not selected — the server only joins
	// the reason in when the column is asked for.
	cancellationReason?: string;
	cancellationReasonDetail?: string;
	travelStatus: string;
	transactionIds: string[];
	route: RouteEntity;
	tripDateTime: Date;
	// Vessel (ferry) name — only populated/shown for staff and above.
	vesselName?: string;
	startStopId: string;
	endStopId: string;

	bookingType: bookingType;

	paidPrice: number;
	totalCost: number;

	// USER INFO
	userId: string;
	name: string;
	companyName?: string;
	email: string;
	userDisplayName?: string;
	isGuest?: boolean;

	// VEHICLE INFO
	vehicleMakeModel: string;
	vehicleLength: string;
	vehicleWeight: string;
	vehicleWidth: string;
	vehicleCharacteristics: string;
	rego: string;
	driverName: string;
	driverPhone: string;

	// TRAILER INFO
	trailerType: string;
	trailerLength: string;
	trailerWeight: string;
	trailerWidth: string;
	trailerCharacteristics: string;

	// PASSENGER INFO
	passengerCounts: { [key: string]: number };
	passengerNames: string;
	/** Fare types with how many are in each — "2 Business, 1 Comfort". Empty where none apply. */
	passengerClasses?: string;
	/** Every seat held on the booking, deck-qualified and comma separated. */
	seats?: string;

	// ADD-ONS
	addOns: string;

	// ADDITIONAL INFO
	additionalInformation: string;
	// Admin-configured dynamic booking field values, keyed by field id (guid).
	dynamicFieldValues?: Record<string, string>;

	// FREIGHT INFO
	freightDescription: string;
	freightDimensions: string;
	freightReceiverName: string;
	freightReceiverContact: string;
	freightNotifyName: string;
	freightNotifyContact: string;

	// EVENT INFO
	eventName: string;
	eventTickets: string;
	eventPrimaryName: string;
	eventPrimaryContact: string;
}

// Helper function to convert BookingStatusTagOption to bookingTransactionStatus
function convertStatusToBackendEnum(status: BookingStatusTagOption): bookingTransactionStatus {
	// Assuming the enum values are the same, just cast them
	// If they're different, you'll need a mapping function
	return status as bookingTransactionStatus;
}

export interface BookingFiltersOptions {
	searchString?: string;
	bookingFilterType: string;
	bookingTypes?: bookingType[];
	travelStatusFilters?: string[];
	// Route ids. Empty / omitted means all routes.
	routeFilters?: string[];
	startDate?: Date;
	endDate?: Date;
	filterByCreatedDate: boolean;
	statusFilters: BookingStatusTagOption[];
	skip?: number;
	take?: number;
	orderBy?: BookingOrderByOption;
	orderByDescending?: boolean;
	userId?: string;
	expandString?: string;
	initialFetch: boolean;
	columns?: string[];
	// Return the page without the total (count comes back null). The staff table fetches the total
	// separately with countOnly, keyed without the page, so rows don't wait on it and paging reuses it.
	skipCount?: boolean;
	countOnly?: boolean;
}

export async function FetchBookingsWithFilters(
	options: BookingFiltersOptions,
): Promise<{ data: BookingListExportDto[]; count: number | null }> {
	const params = CreateBookingFilterParams(options);

	const response = await axios.get(`${FERRY_BOOKING_URL}/fetch-filtered-bookings`, { params });

	return {
		data: response.data.data.map((b: any) => b as BookingListExportDto),
		count: response.data.count,
	};
}

export async function FetchBookingsCount(options: BookingFiltersOptions): Promise<number> {
	const { count } = await FetchBookingsWithFilters({ ...options, countOnly: true });
	return count ?? 0;
}

export function CreateBookingFilterParams(options: BookingFiltersOptions) {
	const params = new URLSearchParams();

	if (stringNotEmpty(options.searchString)) {
		params.append('searchString', options.searchString);
	}

	if (options.statusFilters && options.statusFilters.length > 0) {
		// Convert BookingStatusTagOption to bookingTransactionStatus
		const backendStatusFilters = options.statusFilters.map(convertStatusToBackendEnum);
		// When the Unpaid filter option is hidden (the pay-later feature is off) staff have no way
		// to toggle it, so unpaid bookings would drop out of any explicit status selection. Keep
		// them visible by always including the Unpaid status in the query in that case.
		const unpaidBackendStatus = convertStatusToBackendEnum(BookingStatusTagOption.UNPAID);
		if (!appConfigStore.ShowUnpaidStatusTag && !backendStatusFilters.includes(unpaidBackendStatus)) {
			backendStatusFilters.push(unpaidBackendStatus);
		}
		for (const status of backendStatusFilters) {
			params.append('statusFilters', status);
		}
	}
	if (
		options.bookingTypes &&
		options.bookingTypes.length > 0 &&
		options.bookingTypes.length <= getBookingTypesInApp().length
	) {
		for (const type of options.bookingTypes) {
			params.append('bookingTypes', String(type));
		}
	}
	if (options.travelStatusFilters && options.travelStatusFilters.length > 0) {
		for (const status of options.travelStatusFilters) {
			params.append('travelStatusFilters', status);
		}
	}
	// Empty means "all routes", so nothing is sent and the server skips the clause entirely.
	if (options.routeFilters && options.routeFilters.length > 0) {
		for (const routeId of options.routeFilters) {
			params.append('routeFilters', routeId);
		}
	}
	if (options.bookingFilterType !== undefined) {
		params.append('bookingFilterType', String(options.bookingFilterType));
	}
	if (options.startDate) {
		params.append('startDate', serialiseDateTimeNoTz(options.startDate));
	}
	if (options.endDate) {
		params.append('endDate', serialiseDateTimeNoTz(options.endDate));
	}
	if (options.filterByCreatedDate) {
		params.append('filterByCreatedDate', String(options.filterByCreatedDate));
	}
	if (options.skip !== undefined) {
		params.append('skip', options.skip.toString());
	}
	if (options.take !== undefined) {
		params.append('take', options.take.toString());
	}
	if (options.orderBy !== undefined) {
		params.append('orderBy', options.orderBy.toString());
	}
	if (options.orderByDescending !== undefined) {
		params.append('orderByDescending', String(options.orderByDescending));
	}
	if (options.userId) {
		params.append('userId', options.userId);
	}
	if (options.initialFetch) {
		params.append('initialFetch', String(options.initialFetch));
	}
	if (options.skipCount) {
		params.append('skipCount', 'true');
	}
	if (options.countOnly) {
		params.append('countOnly', 'true');
	}

	// Add columns parameter
	if (options.columns && options.columns.length > 0) {
		for (const column of options.columns) {
			params.append('columns', column);
		}
	}

	return params;
}
